// ── useToast ──
// v0.1.0 stub: in-memory toast queue with auto-dismiss via setTimeout.
// No UI rendering at this layer — Phase 3 / v0.2.0 wires the visual component.
// Surface kept minimal so Provider can be swapped without touching call sites.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const DEFAULT_TTL = 5000;

const noop = () => {};

export const ToastContext = createContext({
  toasts: [],
  push: noop,
  dismiss: noop,
});

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());
  const seqRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timers = timersRef.current;
    const handle = timers.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.delete(id);
    }
  }, []);

  const push = useCallback((toast) => {
    seqRef.current += 1;
    const id = toast && toast.id != null
      ? toast.id
      : `toast_${Date.now()}_${seqRef.current}`;
    const level = (toast && toast.level) || 'info';
    const message = (toast && toast.message) || '';
    const ttl = toast && Number.isFinite(toast.ttl) ? toast.ttl : DEFAULT_TTL;
    const next = { id, level, message, ttl };

    setToasts((prev) => [...prev, next]);

    if (ttl > 0) {
      const handle = setTimeout(() => {
        dismiss(id);
      }, ttl);
      timersRef.current.set(id, handle);
    }

    return id;
  }, [dismiss]);

  // Cleanup any outstanding timers on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((handle) => clearTimeout(handle));
      timers.clear();
    };
  }, []);

  const value = { toasts, push, dismiss };
  return React.createElement(ToastContext.Provider, { value }, children);
}

export function useToast() {
  return useContext(ToastContext);
}
